/**
 * Indicator rule builder (brief AUT-03, 5.3).
 *
 * A rule is an explicit, auditable object: which source values
 * count as yes, which as no, which are missing. Proposals come from
 * value labels (a "Yes" label is evidence), from the preset's
 * dichotomous convention, and from the value set itself — in that
 * order of trust — and are returned as data. Nothing is recoded
 * until a rule is applied through recodeIndicators, which returns a
 * NEW table (indicator columns holding only 0, 1, or null, ready
 * for the io converter) plus a transformation log with the counts
 * the rule produced. Uncovered values are never guessed: they are
 * listed on the rule, and applying an incomplete rule maps them to
 * missing AND reports them in the log.
 */
import { columnIndex } from '@sae/io';
import { compareValues, distinctValuesOf, labelOf } from './profile.js';
const YES_TEXT_RE = /^\s*(yes|y|true)\b/i;
const NO_TEXT_RE = /^\s*(no|n|false)\b/i;
const MISSING_TEXT_RE = /missing|refus|don'?t know|do not know|not applicable|no answer|skipped|not asked/i;
function sameValueSet(a, b) {
    if (a.length !== b.length)
        return false;
    const sa = new Set(a.map(String));
    return b.every((v) => sa.has(String(v)));
}
/**
 * Propose a rule for one column. Sources, strongest first:
 * 1. value labels that read as yes / no / missing;
 * 2. the preset's dichotomous coding, when the value set matches;
 * 3. the bare value set {0,1} or {1,2}.
 * Values no source classifies are reported as uncovered.
 */
export function proposeIndicatorRule(table, column, preset) {
    const values = distinctValuesOf(table, column);
    const reasons = [];
    const yes = new Set();
    const no = new Set();
    const missing = new Set();
    let origin = 'values';
    let confidence = 0.4;
    // 1. Label-declared missingness always wins.
    for (const v of values) {
        const label = labelOf(table, column, v);
        if (label !== undefined && MISSING_TEXT_RE.test(label)) {
            missing.add(v);
            reasons.push(`value ${String(v)} is labeled "${label}": treated as missing`);
        }
    }
    const substantive = values.filter((v) => !missing.has(v));
    // 2. Yes/no from labels.
    let labeledYes = 0;
    let labeledNo = 0;
    for (const v of substantive) {
        const label = labelOf(table, column, v);
        if (label === undefined)
            continue;
        if (YES_TEXT_RE.test(label)) {
            yes.add(v);
            labeledYes++;
        }
        else if (NO_TEXT_RE.test(label)) {
            no.add(v);
            labeledNo++;
        }
    }
    if (labeledYes > 0 && labeledNo > 0 && yes.size + no.size === substantive.length) {
        origin = 'labels';
        confidence = 0.95;
        reasons.push('value labels identify the yes and no values');
    }
    else {
        // 3. Preset dichotomous coding, when the value set matches.
        const dic = preset?.dichotomousRule ?? null;
        if (dic !== null &&
            substantive.length === 2 &&
            sameValueSet(substantive, [...dic.yesValues, ...dic.noValues].filter((v) => !missing.has(v)))) {
            for (const v of dic.yesValues)
                if (!missing.has(v))
                    yes.add(v);
            for (const v of dic.noValues)
                if (!missing.has(v))
                    no.add(v);
            origin = 'preset';
            confidence = 0.85;
            reasons.push(`${preset?.name ?? 'preset'} dichotomous coding: ${dic.yesValues.join(', ')} = yes, ${dic.noValues.join(', ')} = no`);
        }
        else if (substantive.length === 2) {
            // 4. Bare value sets.
            const set = new Set(substantive.map(String));
            if (set.has('0') && set.has('1')) {
                yes.add(substantive.find((v) => String(v) === '1'));
                no.add(substantive.find((v) => String(v) === '0'));
                confidence = 0.8;
                reasons.push('values are 0 and 1: 1 counts as yes');
            }
            else if (set.has('1') && set.has('2')) {
                yes.add(substantive.find((v) => String(v) === '1'));
                no.add(substantive.find((v) => String(v) === '2'));
                confidence = 0.65;
                reasons.push('values are 1 and 2: proposed as 1 = yes, 2 = no (confirm before running)');
            }
        }
    }
    const classified = new Set([...yes, ...no, ...missing]);
    const uncovered = values.filter((v) => !classified.has(v));
    if (uncovered.length > 0) {
        reasons.push(`values not classified by any source: ${uncovered.map(String).join(', ')}`);
        confidence = Math.min(confidence, 0.4);
    }
    const col = table.columns[columnIndex(table, column)];
    return {
        id: column,
        sourceColumn: column,
        ...(col?.label ? { label: col.label } : {}),
        yesValues: [...yes].sort(compareValues),
        noValues: [...no].sort(compareValues),
        missingValues: [...missing].sort(compareValues),
        origin,
        confidence,
        reasons,
        uncoveredValues: uncovered,
        complete: uncovered.length === 0 && yes.size > 0 && no.size > 0,
    };
}
/** Apply one rule to raw values: 1 = yes, 0 = no, null = missing. */
export function applyIndicatorRule(value, rule) {
    if (value === null)
        return null;
    if (rule.yesValues.some((v) => v === value))
        return 1;
    if (rule.noValues.some((v) => v === value))
        return 0;
    return null; // declared missing OR uncovered: both are missing, logged by the recode
}
/**
 * Recode every ruled indicator column of a table to 0/1/null.
 * Returns a new table; the input is not modified. Uncovered values
 * become missing and are counted in the log entry, so the loss is
 * visible (and QC-08 fires on the incomplete rule).
 */
export function recodeIndicators(table, rules) {
    const log = [];
    const indexByColumn = new Map();
    for (const rule of rules) {
        const idx = columnIndex(table, rule.sourceColumn);
        if (idx >= 0)
            indexByColumn.set(rule.sourceColumn, idx);
    }
    const ruleByIndex = new Map();
    for (const rule of rules) {
        const idx = indexByColumn.get(rule.sourceColumn);
        if (idx !== undefined)
            ruleByIndex.set(idx, rule);
    }
    const counts = new Map();
    for (const rule of rules) {
        counts.set(rule.sourceColumn, { yes: 0, no: 0, missing: 0, uncovered: 0 });
    }
    const rows = table.rows.map((row) => row.map((cell, idx) => {
        const rule = ruleByIndex.get(idx);
        if (rule === undefined)
            return cell;
        const c = counts.get(rule.sourceColumn);
        if (cell === null) {
            c.missing++;
            return null;
        }
        const v = cell;
        if (rule.yesValues.some((x) => x === v)) {
            c.yes++;
            return 1;
        }
        if (rule.noValues.some((x) => x === v)) {
            c.no++;
            return 0;
        }
        if (rule.missingValues.some((x) => x === v)) {
            c.missing++;
            return null;
        }
        c.uncovered++;
        return null;
    }));
    for (const rule of rules) {
        const c = counts.get(rule.sourceColumn);
        if (c === undefined)
            continue;
        log.push({
            kind: 'indicator-recode',
            column: rule.sourceColumn,
            ruleId: rule.id,
            counts: {
                yes: c.yes,
                no: c.no,
                missing: c.missing,
                ...(c.uncovered > 0 ? { uncovered: c.uncovered } : {}),
            },
            note: `Recoded "${rule.sourceColumn}" with rule ${rule.id} ` +
                `(yes: ${rule.yesValues.map(String).join(', ') || 'none'}; ` +
                `no: ${rule.noValues.map(String).join(', ') || 'none'}; ` +
                `missing: ${rule.missingValues.map(String).join(', ') || 'none'}).` +
                (c.uncovered > 0
                    ? ` ${c.uncovered} values were not covered by the rule and were treated as missing.`
                    : ''),
        });
    }
    return { table: { ...table, rows }, log };
}
//# sourceMappingURL=rules.js.map